import { type JSX, type ReactNode, useState } from 'react';
import { Link, generatePath } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import {
  CocoOrderType,
  CocoOrdersProductTypeFilterValues,
  CocoOrdersStatusFilterValues,
  RelatedPartyRoles,
} from '../../common/enums.js';
import { useSearchParams } from '../../common/hooks/useSearchParams.js';
import {
  broadbandMsg,
  cancelledMsg,
  changeOrderMsg,
  companyMsg,
  contentMsg,
  dateFromMsg,
  dateOfOrderMsg,
  dateToMsg,
  deliveredMsg,
  elisaDevicesServiceMsg,
  newOrderMsg,
  noOrdersMsg,
  openMsg,
  orderDateMsg,
  orderMsg,
  orderTypeMsg,
  otherProductsMsg,
  partiallyDeliveredMsg,
  pendingMsg,
  phoneSubscriptionMsg,
  processingMsg,
  productsIncludedMsg,
  purchasedDeviceMsg,
  scheduledMsg,
  serviceMsg,
  statusMsg,
  t,
  topicMsg,
  userMsg,
} from '../../common/i18n';
import { formatTimeStampToHHmm, formatTimestampToDDMMYYYY } from '../../common/utils/dateUtils.js';
import { CocoOrderUrlParams } from '../../public/site/path/SelfService/OrderHistory/customerOrderConstants.js';
import { ConsolidatedListView } from '../ConsolidatedListView/ConsolidatedListView.js';
import { Highlight } from '../Highlight/Highlight.js';
import { Status } from '../Status/Status.js';
import { SearchFiltersCoco } from '../Table/SearchFiltersCoco.js';
import {
  DEFAULT_ITEMS_PER_PAGE,
  type DefaultListSearchParams,
  ListPagination,
  ListSearch,
  Table,
  TableDescription,
} from '../Table/index.js';
import { setDefaultItemsPerPage } from '../Table/tableUtils.js';
import { getCompanyName, getOrderStateInfo, getOrderType, getPartyName, getTopicText } from './cocoOrderUtils.js';
import type { CLTableColumn } from '../../common/types/componentLibrary.js';
import type { CocoProductOrderSummary } from '../../generated/api/cocoProductOrderSummary.js';
import type { AuthenticatedUser } from '../../public/site/authState.js';
import type { SearchFilterGroup } from '../Table/SearchFilters.js';

export interface CustomerOrderListCoCoProps {
  basePath: string;
  orders: CocoProductOrderSummary[];
  totalItems: number;
  loading?: boolean;
  searchQuery?: string;
  authenticatedUser?: AuthenticatedUser;
}

const getOrderContent = (order: CocoProductOrderSummary) => {
  return order.productOrderItem
    .map(item => (item.primary === true ? item.productOffering.name : ''))
    .filter(name => name !== '');
};

const getOrderUrl = (basePath: string, orderId: string) => generatePath(basePath, { orderId });

const getFilterGroup = (
  label: () => string,
  value: string,
  options: Array<{ label: () => string; value: string }>,
  selectedValues?: string
): SearchFilterGroup => {
  const selected = new Set(selectedValues?.split('|') ?? []);
  return {
    label,
    value,
    items: options.map(option => ({ ...option, checked: selected.has(option.value) })),
  };
};

export const CustomerOrderListCoCo = ({
  basePath,
  orders,
  totalItems,
  loading,
  searchQuery,
  authenticatedUser,
}: CustomerOrderListCoCoProps): JSX.Element => {
  const searchParams = useSearchParams<
    DefaultListSearchParams & {
      state?: string;
      productType?: string;
      orderType?: string;
    }
  >();
  const searchTerms = searchQuery?.split(' ') || [];
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filterGroups: SearchFilterGroup[] = [
    getFilterGroup(
      () => t.ASQT(statusMsg),
      CocoOrderUrlParams.ORDER_STATUS,
      [
        { label: () => t.PQLL(pendingMsg), value: CocoOrdersStatusFilterValues.PENDING },
        { label: () => t.VZZW(processingMsg), value: CocoOrdersStatusFilterValues.PROCESSING },
        { label: () => t.SJ7E(partiallyDeliveredMsg), value: CocoOrdersStatusFilterValues.PARTIALLY_DELIVERED },
        { label: () => t.PQGG(deliveredMsg), value: CocoOrdersStatusFilterValues.DELIVERED },
        { label: () => t.TJ54(openMsg), value: CocoOrdersStatusFilterValues.OPEN },
        { label: () => t.PEKP(cancelledMsg), value: CocoOrdersStatusFilterValues.CANCELLED },
        { label: () => t.PY03(scheduledMsg), value: CocoOrdersStatusFilterValues.SCHEDULED },
      ],
      searchParams.state
    ),
    getFilterGroup(
      () => t.BIS7(productsIncludedMsg),
      CocoOrderUrlParams.PRODUCT_TYPE,
      [
        { label: () => t.R6AR(phoneSubscriptionMsg), value: CocoOrdersProductTypeFilterValues.MOBILE },
        { label: () => t.FV4B(broadbandMsg), value: CocoOrdersProductTypeFilterValues.BROADBAND },
        { label: () => t.JJM2(elisaDevicesServiceMsg), value: CocoOrdersProductTypeFilterValues.EPP },
        { label: () => t.QL8F(purchasedDeviceMsg), value: CocoOrdersProductTypeFilterValues.DEVICE },
        { label: () => t.CDME(serviceMsg), value: CocoOrdersProductTypeFilterValues.SERVICE },
        { label: () => t.K2PG(otherProductsMsg), value: CocoOrdersProductTypeFilterValues.OTHER },
      ],
      searchParams.productType
    ),
    getFilterGroup(
      () => t.QDKP(orderTypeMsg),
      CocoOrderUrlParams.ORDER_TYPE,
      [
        { label: () => t.PTQ7(newOrderMsg), value: CocoOrderType.NEW_ORDER },
        { label: () => t.PZ9O(changeOrderMsg), value: CocoOrderType.CHANGE_ORDER },
      ],
      searchParams.orderType
    ),
  ];

  const columns: CLTableColumn[] = [
    { key: 'id', label: t.C001(orderMsg), sortable: true, width: '15%' },
    { key: 'created', label: t.V1HP(orderDateMsg), sortable: true, width: '12%' },
    { key: 'content', label: t.NBNH(contentMsg), sortable: false, width: '18%' },
    { key: 'topic', label: t.JJTS(topicMsg), sortable: false, width: '13%' },
    { key: 'recipient', label: t.LUZM(userMsg), sortable: false, width: '17%' },
    { key: 'status', label: t.ASQT(statusMsg), sortable: true, width: '10%' },
    { key: 'company', label: t.KJTS(companyMsg), sortable: false, width: '15%' },
  ];

  const rows: Record<string, ReactNode>[] = orders.map(order => {
    const statusInfo = getOrderStateInfo(order.orderState);
    const created = Date.parse(order.created);
    const recipientName = getPartyName(order, RelatedPartyRoles.ASSIGNEE);
    const items = getOrderContent(order).join(', ');

    return {
      id: (
        <>
          <Link to={getOrderUrl(basePath, order.id)}>
            <Highlight text={order.id} highlights={searchTerms} />
          </Link>
          <TableDescription description={getOrderType(order)} />
        </>
      ),
      created: (
        <TableDescription
          description={Number.isNaN(created) ? '' : formatTimeStampToHHmm(created)}
          title={Number.isNaN(created) ? '' : formatTimestampToDDMMYYYY(created)}
        />
      ),
      content: <Highlight text={items} highlights={searchTerms} />,
      topic: <TableDescription title={getTopicText(order)} />,
      recipient: <Highlight text={recipientName} highlights={searchTerms} />,
      status: <Status color={statusInfo.color} textAfterBadge={statusInfo.text} inTable={true} />,
      company: getCompanyName(order),
    };
  });

  return (
    <div className={`of-customer-order-list ${dsClass.MARGIN_TOP_4}`}>
      <ConsolidatedListView
        authenticatedUser={authenticatedUser}
        listElement={
          <Table columns={columns} isLoading={loading} noItemsText={t.QFXU(noOrdersMsg)} rows={rows || []} />
        }
        listPagination={<ListPagination totalItems={totalItems} />}
        listSearch={
          <ListSearch
            filtersOpen={filtersOpen}
            setFiltersOpen={setFiltersOpen}
            onSearch={() => setDefaultItemsPerPage(new URLSearchParams(searchParams), DEFAULT_ITEMS_PER_PAGE)}
          />
        }
        searchFilters={
          <SearchFiltersCoco
            displayActiveFiltersAsTiles={!filtersOpen}
            filterGroups={filterGroups}
            dateRange={{
              label: () => t.F1NC(dateOfOrderMsg),
              fromLabel: () => t.HSC5(dateFromMsg),
              toLabel: () => t.AAX0(dateToMsg),
              fromParam: CocoOrderUrlParams.ORDER_DATE_FROM,
              toParam: CocoOrderUrlParams.ORDER_DATE_TO,
            }}
            onModalClose={() => setFiltersOpen(false)}
          />
        }
      />
    </div>
  );
};
